import { useEffect, useState } from "react";

interface QuarterlyData {
  year: number;
  q1: number | null;
  q2: number | null;
  q3: number | null;
  q4: number | null;
  annual: number | null;
}

// Fetch BTC historical data and calculate quarterly returns
async function fetchQuarterlyData(): Promise<QuarterlyData[]> {
  try {
    const response = await fetch(
      "https://api.coingecko.com/api/v3/coins/bitcoin/market_chart?vs_currency=usd&days=5000"
    );
    const data = await response.json();
    const prices = data?.prices as [number, number][];
    
    if (!prices || prices.length === 0) return [];
    
    const quarterlyMap = new Map<string, number>();
    
    for (const [timestamp, price] of prices) {
      const date = new Date(timestamp);
      const year = date.getFullYear();
      const quarter = Math.floor(date.getMonth() / 3) + 1;
      const key = `${year}-Q${quarter}`;
      quarterlyMap.set(key, price);
    }
    
    const sortedKeys = Array.from(quarterlyMap.keys()).sort();
    const results: QuarterlyData[] = [];
    
    for (let i = 1; i < sortedKeys.length; i++) {
      const prevKey = sortedKeys[i - 1];
      const currKey = sortedKeys[i];
      const prevPrice = quarterlyMap.get(prevKey)!;
      const currPrice = quarterlyMap.get(currKey)!;
      
      const returnPct = ((currPrice - prevPrice) / prevPrice) * 100;
      const [year, q] = currKey.split("-Q").map(Number);
      
      let yearData = results.find(r => r.year === year);
      if (!yearData) {
        yearData = { year, q1: null, q2: null, q3: null, q4: null, annual: null };
        results.push(yearData);
      }
      
      if (q === 1) yearData.q1 = returnPct;
      if (q === 2) yearData.q2 = returnPct;
      if (q === 3) yearData.q3 = returnPct;
      if (q === 4) yearData.q4 = returnPct;
    }
    
    // Calculate annual returns
    for (const row of results) {
      if (row.q1 !== null && row.q4 !== null) {
        const startPrice = await getPriceForYearStart(row.year);
        const endPrice = await getPriceForYearEnd(row.year);
        if (startPrice && endPrice) {
          row.annual = ((endPrice - startPrice) / startPrice) * 100;
        }
      }
    }
    
    return results.sort((a, b) => b.year - a.year);
  } catch (error) {
    console.error("Error fetching BTC data:", error);
    return [];
  }
}

async function getPriceForYearStart(year: number): Promise<number | null> {
  try {
    const date = new Date(year, 0, 1).getTime() / 1000;
    const response = await fetch(
      `https://api.coingecko.com/api/v3/coins/bitcoin/history?date=${new Date(date * 1000).toISOString().split('T')[0]}`
    );
    const data = await response.json();
    return data?.market_data?.current_price?.usd || null;
  } catch {
    return null;
  }
}

async function getPriceForYearEnd(year: number): Promise<number | null> {
  try {
    const date = new Date(year, 11, 31).getTime() / 1000;
    const response = await fetch(
      `https://api.coingecko.com/api/v3/coins/bitcoin/history?date=${new Date(date * 1000).toISOString().split('T')[0]}`
    );
    const data = await response.json();
    return data?.market_data?.current_price?.usd || null;
  } catch {
    return null;
  }
}

const FALLBACK_DATA: QuarterlyData[] = [
  { year: 2026, q1: -22.2, q2: -14.09, q3: 42.71, q4: 0.98, annual: 4.2 },
  { year: 2025, q1: -11.82, q2: 29.74, q3: 6.31, q4: -23.07, annual: -4.2 },
  { year: 2024, q1: 68.68, q2: -11.92, q3: 0.96, q4: 47.73, annual: 118.5 },
  { year: 2023, q1: 71.77, q2: 7.19, q3: -11.54, q4: 56.9, annual: 155.2 },
  { year: 2022, q1: -1.46, q2: -56.2, q3: -2.57, q4: -14.75, annual: -64.8 },
  { year: 2021, q1: 103.17, q2: -40.36, q3: 25.01, q4: 5.45, annual: 59.5 },
  { year: 2020, q1: -10.83, q2: 42.33, q3: 17.97, q4: 168.02, annual: 301.2 },
  { year: 2019, q1: 8.74, q2: 159.36, q3: -22.86, q4: -13.54, annual: 93.8 },
  { year: 2018, q1: -49.7, q2: -7.71, q3: 3.61, q4: -42.16, annual: -72.6 },
  { year: 2017, q1: 11.89, q2: 123.86, q3: 80.41, q4: 215.07, annual: 1331.5 },
  { year: 2016, q1: -3.06, q2: 62.06, q3: -9.41, q4: 58.17, annual: 124.7 },
  { year: 2015, q1: -24.14, q2: 7.57, q3: -10.05, q4: 81.24, annual: 35.7 },
  { year: 2014, q1: -37.42, q2: 40.43, q3: -39.74, q4: -16.7, annual: -57.5 },
  { year: 2013, q1: 539.96, q2: -3.97, q3: 40.6, q4: 479.59, annual: 5305.2 },
];

function calculateStats(data: QuarterlyData[]) {
  const quarters = ['q1', 'q2', 'q3', 'q4'] as const;
  const stats: { avg: number; median: number }[] = [];
  
  for (const q of quarters) {
    const values = data.map(d => d[q]).filter((v): v is number => v !== null);
    const avg = values.reduce((a, b) => a + b, 0) / values.length;
    const sorted = [...values].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    const median = sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
    stats.push({ avg, median });
  }
  
  // Annual stats
  const annualValues = data.map(d => d.annual).filter((v): v is number => v !== null);
  const annualAvg = annualValues.reduce((a, b) => a + b, 0) / annualValues.length;
  const annualSorted = [...annualValues].sort((a, b) => a - b);
  const annualMid = Math.floor(annualSorted.length / 2);
  const annualMedian = annualSorted.length % 2 ? annualSorted[annualMid] : (annualSorted[annualMid - 1] + annualSorted[annualMid]) / 2;
  stats.push({ avg: annualAvg, median: annualMedian });
  
  return stats;
}

function getColor(value: number | null): string {
  if (value === null) return 'transparent';
  if (value > 0) return 'rgb(81, 204, 139)';
  if (value < 0) return 'rgb(251, 113, 113)';
  return 'rgb(153, 153, 153)';
}

function formatValue(value: number | null): string {
  if (value === null) return '-';
  return value > 0 ? `+${value.toFixed(2)}%` : `${value.toFixed(2)}%`;
}

export default function BTCQuarterlyReturns() {
  const [data, setData] = useState<QuarterlyData[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      const quarterly = await fetchQuarterlyData();
      setData(quarterly.length > 0 ? quarterly : FALLBACK_DATA);
      setLoading(false);
    };
    load();
  }, []);

  const stats = calculateStats(data);

  return (
    <div className="min-h-screen bg-gray-900 text-white p-4 md:p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold">📊 Bitcoin Quarterly Returns (%)</h1>
        <p className="text-gray-400 text-sm mt-1">BTC performance by quarter since 2013</p>
      </div>

      <div className="grid grid-cols-5 gap-4 mb-6">
        <div className="bg-gray-800 rounded-xl border border-gray-700 p-4">
          <p className="text-gray-400 text-xs uppercase tracking-wide">Q1 Avg</p>
          <p className={`text-2xl font-bold ${stats[0]?.avg >= 0 ? "text-green-400" : "text-red-400"}`}>
            {stats[0]?.avg >= 0 ? "+" : ""}{stats[0]?.avg?.toFixed(2) || 0}%
          </p>
        </div>
        <div className="bg-gray-800 rounded-xl border border-gray-700 p-4">
          <p className="text-gray-400 text-xs uppercase tracking-wide">Q2 Avg</p>
          <p className={`text-2xl font-bold ${stats[1]?.avg >= 0 ? "text-green-400" : "text-red-400"}`}>
            {stats[1]?.avg >= 0 ? "+" : ""}{stats[1]?.avg?.toFixed(2) || 0}%
          </p>
        </div>
        <div className="bg-gray-800 rounded-xl border border-gray-700 p-4">
          <p className="text-gray-400 text-xs uppercase tracking-wide">Q3 Avg</p>
          <p className={`text-2xl font-bold ${stats[2]?.avg >= 0 ? "text-green-400" : "text-red-400"}`}>
            {stats[2]?.avg >= 0 ? "+" : ""}{stats[2]?.avg?.toFixed(2) || 0}%
          </p>
        </div>
        <div className="bg-gray-800 rounded-xl border border-gray-700 p-4">
          <p className="text-gray-400 text-xs uppercase tracking-wide">Q4 Avg</p>
          <p className={`text-2xl font-bold ${stats[3]?.avg >= 0 ? "text-green-400" : "text-red-400"}`}>
            {stats[3]?.avg >= 0 ? "+" : ""}{stats[3]?.avg?.toFixed(2) || 0}%
          </p>
        </div>
        <div className="bg-gray-800 rounded-xl border border-gray-700 p-4">
          <p className="text-gray-400 text-xs uppercase tracking-wide">Annual Avg</p>
          <p className={`text-2xl font-bold ${stats[4]?.avg >= 0 ? "text-green-400" : "text-red-400"}`}>
            {stats[4]?.avg >= 0 ? "+" : ""}{stats[4]?.avg?.toFixed(2) || 0}%
          </p>
        </div>
      </div>

      <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
        <div className="grid grid-cols-6 gap-0 border-b border-gray-700">
          <div className="p-3 text-gray-400 font-medium text-sm">Time</div>
          <div className="p-3 text-center text-gray-400 font-medium text-sm">Q1</div>
          <div className="p-3 text-center text-gray-400 font-medium text-sm">Q2</div>
          <div className="p-3 text-center text-gray-400 font-medium text-sm">Q3</div>
          <div className="p-3 text-center text-gray-400 font-medium text-sm">Q4</div>
          <div className="p-3 text-center text-gray-400 font-medium text-sm">Annual</div>
        </div>

        {loading ? (
          <div className="p-8 text-center text-gray-400">Loading quarterly data...</div>
        ) : (
          <>
            {data.map((row) => (
              <div key={row.year} className="grid grid-cols-6 gap-0 border-b border-gray-700/50">
                <div className="p-3 text-gray-300 font-medium">{row.year}</div>
                <div className="p-3 text-center font-mono font-medium" style={{ backgroundColor: getColor(row.q1), color: 'rgb(255,255,255)' }}>
                  {formatValue(row.q1)}
                </div>
                <div className="p-3 text-center font-mono font-medium" style={{ backgroundColor: getColor(row.q2), color: 'rgb(255,255,255)' }}>
                  {formatValue(row.q2)}
                </div>
                <div className="p-3 text-center font-mono font-medium" style={{ backgroundColor: getColor(row.q3), color: 'rgb(255,255,255)' }}>
                  {formatValue(row.q3)}
                </div>
                <div className="p-3 text-center font-mono font-medium" style={{ backgroundColor: getColor(row.q4), color: 'rgb(255,255,255)' }}>
                  {formatValue(row.q4)}
                </div>
                <div className="p-3 text-center font-mono font-bold" style={{ backgroundColor: getColor(row.annual), color: 'rgb(255,255,255)' }}>
                  {formatValue(row.annual)}
                </div>
              </div>
            ))}

            <div className="grid grid-cols-6 gap-0 border-b border-gray-700 bg-gray-700/30">
              <div className="p-3 text-gray-300 font-semibold">Average</div>
              <div className="p-3 text-center font-mono font-semibold" style={{ backgroundColor: 'rgba(153, 153, 153, 0.64)', color: 'rgb(255,255,255)' }}>
                {formatValue(stats[0]?.avg)}
              </div>
              <div className="p-3 text-center font-mono font-semibold" style={{ backgroundColor: 'rgba(153, 153, 153, 0.64)', color: 'rgb(255,255,255)' }}>
                {formatValue(stats[1]?.avg)}
              </div>
              <div className="p-3 text-center font-mono font-semibold" style={{ backgroundColor: 'rgba(153, 153, 153, 0.64)', color: 'rgb(255,255,255)' }}>
                {formatValue(stats[2]?.avg)}
              </div>
              <div className="p-3 text-center font-mono font-semibold" style={{ backgroundColor: 'rgba(153, 153, 153, 0.64)', color: 'rgb(255,255,255)' }}>
                {formatValue(stats[3]?.avg)}
              </div>
              <div className="p-3 text-center font-mono font-semibold" style={{ backgroundColor: 'rgba(153, 153, 153, 0.64)', color: 'rgb(255,255,255)' }}>
                -
              </div>
            </div>

            <div className="grid grid-cols-6 gap-0 bg-gray-700/30">
              <div className="p-3 text-gray-300 font-semibold">Median</div>
              <div className="p-3 text-center font-mono font-semibold" style={{ backgroundColor: 'rgba(153, 153, 153, 0.64)', color: 'rgb(255,255,255)' }}>
                {formatValue(stats[0]?.median)}
              </div>
              <div className="p-3 text-center font-mono font-semibold" style={{ backgroundColor: 'rgba(153, 153, 153, 0.64)', color: 'rgb(255,255,255)' }}>
                {formatValue(stats[1]?.median)}
              </div>
              <div className="p-3 text-center font-mono font-semibold" style={{ backgroundColor: 'rgba(153, 153, 153, 0.64)', color: 'rgb(255,255,255)' }}>
                {formatValue(stats[2]?.median)}
              </div>
              <div className="p-3 text-center font-mono font-semibold" style={{ backgroundColor: 'rgba(153, 153, 153, 0.64)', color: 'rgb(255,255,255)' }}>
                {formatValue(stats[3]?.median)}
              </div>
              <div className="p-3 text-center font-mono font-semibold" style={{ backgroundColor: 'rgba(153, 153, 153, 0.64)', color: 'rgb(255,255,255)' }}>
                -
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}